/**
 * Date parsing utilities for TradesMatePro
 * Properly handles PostgreSQL date type across all user timezones
 */

/**
 * Parse a date value from Supabase/PostgreSQL
 * PostgreSQL date columns return as YYYY-MM-DD (interpreted as UTC midnight).
 * We need to treat them as "date only" - no timezone - and display in user's local timezone.
 * 
 * @param {string|null|undefined} dateValue - The date value from DB
 * @returns {Date|null} - Parsed Date object in local time
 */
export const parseDbDate = (dateValue) => {
  if (!dateValue) return null;
  
  const dateStr = String(dateValue);
  
  // Check if it's a pure date string (YYYY-MM-DD) - PostgreSQL date type
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    // Split the date and create a local date (no timezone)
    // This ensures the date displays correctly in the user's timezone
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day); // Local time, no UTC conversion
  }
  
  // Has time component (timestamp), parse normally (supabase sends as UTC)
  return new Date(dateValue);
};

/**
 * Coerce an <input type="date"> value to a DB-safe date string ('YYYY-MM-DD') or null.
 *
 * Empty inputs MUST become null: Postgres rejects '' for a `date` column with
 * `22007 invalid input syntax for type date: ""`. Valid 'YYYY-MM-DD' values pass straight
 * through (no behavior change); anything else parseable is normalized to a local YYYY-MM-DD.
 * Use this on the payload for any `date`-column write sourced from a form field.
 *
 * @param {string|Date|null|undefined} value
 * @returns {string|null}
 */
export const toDbDate = (value) => {
  if (value === null || value === undefined) return null;
  const s = String(value).trim();
  if (s === '') return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s; // already what <input type="date"> emits
  const d = value instanceof Date ? value : parseDbDate(value);
  if (!d || isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * Coerce a datetime value (e.g. <input type="datetime-local">) for a `timestamp`/`timestamptz`
 * column: empty -> null, otherwise passed through UNCHANGED. Postgres already parses valid
 * timestamp strings; we deliberately do NOT reformat, to avoid shifting the timezone semantics
 * of values that currently write correctly. This only fixes the empty-string 22007 case.
 *
 * @param {string|Date|null|undefined} value
 * @returns {string|Date|null}
 */
export const toDbTimestamp = (value) => {
  if (value === null || value === undefined) return null;
  return String(value).trim() === '' ? null : value;
};

/**
 * Format a UTC/ISO timestamp (from a `timestamptz` column) as the VALUE for an
 * <input type="datetime-local"> — the user's LOCAL wall-clock 'YYYY-MM-DDTHH:mm'.
 *
 * A datetime-local input has NO timezone; the browser treats its value as local time. The old
 * hand-copied `new Date(ts).toISOString().slice(0,16)` fed UTC into it, so a 7:35am job displayed
 * as 2:35pm. This uses LOCAL getters so the input shows the correct local time. ALWAYS pair with
 * fromDateTimeLocalInput() on the input's onChange so form state round-trips consistently.
 * (Uses the browser's timezone — the standard for datetime-local inputs.)
 *
 * @param {string|Date|null|undefined} value  UTC timestamp / ISO string / Date
 * @returns {string}  'YYYY-MM-DDTHH:mm' (local) or '' when empty/invalid
 */
export const toDateTimeLocalInput = (value) => {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Convert an <input type="datetime-local"> value (local wall-clock 'YYYY-MM-DDTHH:mm') to a UTC
 * ISO string for a `timestamptz` column, or null when empty. `new Date('YYYY-MM-DDTHH:mm')` (no tz
 * suffix) is parsed as LOCAL by JS, so .toISOString() yields the correct UTC instant. Store the
 * RESULT in form state so it round-trips through toDateTimeLocalInput() cleanly.
 *
 * @param {string|Date|null|undefined} value
 * @returns {string|null}  UTC ISO string or null
 */
export const fromDateTimeLocalInput = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
};

/**
 * Format a date for display - uses user's local timezone
 * @param {string|Date|null} dateValue - Date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date or 'N/A'
 */
export const formatDate = (dateValue, options = {}) => {
  if (!dateValue) return 'N/A';
  
  const date = dateValue instanceof Date ? dateValue : parseDbDate(dateValue);
  if (!date || isNaN(date.getTime())) return 'N/A';
  
  const defaultOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...options
  };
  
  return date.toLocaleDateString('en-US', defaultOptions);
};

/**
 * Format a WALL-CLOCK timestamp — one stored as UTC but MEANING local time.
 *
 * DO NOT REPLACE CALLS TO THIS WITH formatDate(). That has now happened twice on the same
 * bug and this helper exists to stop it happening a third time:
 *   d90ee8002  fixed employee_time_off displaying a day early, by stripping the time
 *              component before formatting.
 *   b392b8440  deleted that "for consistent date handling" in favour of formatDate — which
 *              reintroduced the identical bug (refiled as F-EMP-2026-08-31-2340-1).
 * The inline fix looked like an inconsistency, so it was tidied away. A named helper with
 * this note is the same fix that survives being tidied.
 *
 * THE CONVENTION IT SERVES. `employee_time_off.starts_at/ends_at` are timestamptz, but every
 * writer sends a NAIVE string — TimeOffPanel writes '2026-03-09', PTOEditModal writes
 * '2026-03-09T09:00:00' — which Postgres stores at UTC. Readers then use getUTCHours()
 * (PTOEditModal:26). So 9am means 09:00Z: the wall clock the user typed, parked in UTC.
 * Reading it in BROWSER-LOCAL time is what shifts midnight back to the previous day.
 *
 * This is the same separate naive-local model CLAUDE.md §19 flags for marketplace
 * availability — "do NOT fix them with the UTC helpers". Formatting in UTC is not a
 * timezone bug; it is how you read this convention correctly.
 *
 * These columns are deliberately NOT `date`: PTO supports PARTIAL days (PTOEditModal's
 * isFullDay toggle with start/end times), so the time component is load-bearing.
 *
 * @param {string|Date|null} dateValue
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date, or '-' when absent
 */
export const formatWallClockDate = (dateValue, options = {}) => {
  if (!dateValue) return '-';
  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...options
  });
};

/**
 * Format a date with time for display
 * @param {string|Date|null} dateValue - Date to format
 * @returns {string} Formatted date/time or 'N/A'
 */
export const formatDateTime = (dateValue) => {
  if (!dateValue) return 'N/A';
  
  const date = dateValue instanceof Date ? dateValue : parseDbDate(dateValue);
  if (!date || isNaN(date.getTime())) return 'N/A';
  
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};
